import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Hammer, Calendar, DollarSign, FileText, Loader2, AlertCircle, Info } from "lucide-react";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { outsourceToolingMutate } from "@/lib/trpc-helpers";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatNumber } from "@/lib/utils";

import { toVnDateString } from "@apv-erp/status";
export interface OutsourceToolingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: {
    id: string;
    moldCode?: string;
    name?: string;
    productName?: string;
    processName?: string;
    toolingType?: string;
    dimensions?: string;
    productionOrderId?: string;
    poCode?: string;
    customerName?: string;
    defaultUnitPrice?: number;
    expectedDate?: string;
  } | null;
  onSuccess?: (result?: any) => void;
}

export default function OutsourceToolingModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: OutsourceToolingModalProps) {
  const queryClient = useQueryClient();
  const [vendorId, setVendorId] = useState("");
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when item opens
  useEffect(() => {
    if (open && item) {
      setVendorId("");
      setUnitPrice(item.defaultUnitPrice || 1500000); // 1.5M default mold estimate
      // Default expected date: 3 days from now
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 3);
      setExpectedDate(toVnDateString(defaultDate));
      setNotes(
        item.dimensions
          ? `Gia công khuôn kích thước: ${item.dimensions}. Lưỡi dao bế tiêu chuẩn 23.8mm, đế gỗ thông ghép cao cấp.`
          : "Gia công khuôn bế tiêu chuẩn theo bản vẽ kỹ thuật."
      );
    }
  }, [open, item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) return;

    if (!vendorId) {
      toast.error("Vui lòng chọn đối tác / nhà cung cấp gia công khuôn");
      return;
    }

    if (!expectedDate) {
      toast.error("Vui lòng chọn ngày hẹn giao khuôn");
      return;
    }

    if (unitPrice < 0) {
      toast.error("Đơn giá gia công không được âm");
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await outsourceToolingMutate({
        toolingId: item.id,
        vendorId,
        unitPrice: Number(unitPrice) || 0,
        expectedDate,
        notes: notes.trim() || undefined,
      });

      toast.success(
        `Đã tạo đơn gia công khuôn ngoài (${result?.outsourcePO?.poId || "PO"}) thành công!`
      );
      queryClient.invalidateQueries({ queryKey: ["tooling-tracking-board"] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["mold"] });
      queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
      queryClient.invalidateQueries({ queryKey: ["productionOrder"] });

      onOpenChange(false);
      if (onSuccess) onSuccess(result);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi tạo đơn đặt gia công khuôn");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-700">
            <Hammer className="h-5 w-5 text-indigo-600" />
            Đặt Làm Khuôn Ngoài (Maker Outsource PO)
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Thiết lập đơn đặt hàng gia công khuôn với đối tác bên ngoài, ghi nhận chi phí dự kiến và hạn giao để kiểm soát tiến độ Lệnh sản xuất.
          </DialogDescription>
        </DialogHeader>

        {item && (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            {/* Mold Summary Card */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">Khuôn cần đặt:</span>
                <Badge variant="outline" className="font-mono text-xs border-indigo-200 bg-indigo-50 text-indigo-700 font-bold">
                  {item.moldCode || item.name || "Khuôn mới"}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-slate-400">Công đoạn: </span>
                  <span className="font-medium text-slate-800">{item.processName || item.toolingType || "Bế đứt (Die-Cut)"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Kích thước: </span>
                  <span className="font-mono font-medium text-slate-800">{item.dimensions || "Theo mẫu"}</span>
                </div>
                {item.poCode && (
                  <div className="col-span-2">
                    <span className="text-slate-400">Lệnh SX: </span>
                    <span className="font-mono font-bold text-indigo-600">{item.poCode}</span>
                    {item.productName && <span className="text-slate-600"> ({item.productName})</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Vendor Selection */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                Nhà Cung Cấp / Đối Tác Gia Công Khuôn <span className="text-rose-500">*</span>
              </Label>
              <SharedCombobox
                type="vendor"
                value={vendorId}
                onChange={(id) => setVendorId(id)}
                placeholder="Chọn xưởng gia công khuôn (Kim Cương, Tân Á, Á Châu...)"
              />
            </div>

            {/* Cost and Delivery Date */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                  <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                  Đơn Giá Dự Kiến (VNĐ) <span className="text-rose-500">*</span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="10000"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(Number(e.target.value))}
                  placeholder="1,500,000"
                  className="font-mono text-sm"
                  required
                />
                <div className="text-[11px] text-slate-500 font-mono">
                  = {formatNumber(unitPrice)} đ
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  Ngày Hẹn Giao Khuôn <span className="text-rose-500">*</span>
                </Label>
                <Input
                  type="date"
                  value={expectedDate}
                  onChange={(e) => setExpectedDate(e.target.value)}
                  className="text-sm font-medium"
                  required
                />
              </div>
            </div>

            {/* Technical Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                Yêu Cầu Kỹ Thuật Cho Maker
              </Label>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Quy cách lưỡi dao, mút xốp chống kẹt giấy, loại ván ép cao cấp..."
                className="text-xs"
              />
            </div>

            {/* Info Banner */}
            <div className="bg-amber-50/70 border border-amber-200 rounded p-2.5 flex items-start gap-2 text-xs text-amber-800">
              <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                Hệ thống sẽ tự động tạo đơn <strong>Gia công ngoài (Outsource PO)</strong> loại <code>TOOLING</code> và chuyển trạng thái khuôn sang <strong>[Đang Làm Ngoài]</strong> để xưởng theo dõi.
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Đang Tạo Đơn...
                  </>
                ) : (
                  <>
                    <Hammer className="h-4 w-4 mr-1.5" /> Đặt Làm Khuôn
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
